import React from "react";
import {
  AbsoluteFill,
  Img,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { useCopy } from "../copy";
import { BRANDS, type Brand } from "../data/brands";
import { LOOK, TYPE } from "../look";
import { type Stage, useStage } from "../stage";
import { Words } from "../ui/Words";
import { Ticks } from "../../b2b/ui/Sfx";

/**
 * Brand wall — the /brands hub in one shot, built from the live snapshot
 * (`npm run sync:brands`). A tilted grid of logo tiles (the merchants'
 * square logos, full-bleed like app icons) drifting slowly, and a reward
 * tag popping onto a few tiles' corners: the most that brand pays per
 * recommendation (the backend's `reward`, a max).
 */

type Grid = {
  cols: number;
  rows: number;
  tile: number;
  gap: number;
  /** Rows the tag checkerboard spans. */
  tagRows: number;
  /** Wall tilt (deg); flatter when wide so the far end doesn't dip. */
  tilt: number;
};

/** Square is the validated master; the others keep its look. */
const GRIDS: Record<Stage["shape"], Grid> = {
  square: { cols: 4, rows: 3, tile: 216, gap: 50, tagRows: 2, tilt: -10 },
  tall: { cols: 3, rows: 4, tile: 250, gap: 56, tagRows: 4, tilt: -10 },
  wide: { cols: 6, rows: 2, tile: 216, gap: 50, tagRows: 2, tilt: -6 },
};

type Cell = { brand: Brand; row: number; col: number };

// Fill row by row; with fewer brands than cells, the repeats land last
// (bottom-right, the part of the wall the frame crops).
const cellsOf = (g: Grid): Cell[] =>
  Array.from({ length: g.cols * g.rows }, (_, i) => ({
    brand: BRANDS[i % BRANDS.length],
    row: Math.floor(i / g.cols),
    col: i % g.cols,
  }));

// A few tags, not one per tile: 4, spread evenly over a checkerboard,
// popping in reading order like v1.
const TAG_COUNT = 4;
const taggedOf = (g: Grid, cells: Cell[]): Cell[] => {
  const board = cells.filter((c) => c.row < g.tagRows && (c.row + c.col) % 2 === 0);
  if (board.length <= TAG_COUNT) return board;
  return Array.from(
    { length: TAG_COUNT },
    (_, i) => board[Math.round((i * (board.length - 1)) / (TAG_COUNT - 1))],
  );
};

/** Title block height above the grid (two lines + breathing room). */
const TITLE_BLOCK = 324;
const TAGS_AT = 14;
const TAG_STAGGER = 5;

const RewardTag: React.FC<{ amount: number; at: number }> = ({ amount, at }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const copy = useCopy();
  const p = spring({
    frame: frame - at,
    fps,
    config: { damping: 11, stiffness: 240, mass: 0.5 },
  });
  return (
    <div
      style={{
        position: "absolute",
        top: -16,
        right: -14,
        display: "flex",
        padding: "5px 16px 7px",
        borderRadius: 999,
        background: LOOK.green,
        color: LOOK.white,
        whiteSpace: "nowrap",
        boxShadow: "0 8px 18px -6px rgba(5, 114, 62, 0.5)",
        transformOrigin: "85% 50%",
        scale: String(p),
        rotate: `${(1 - p) * 14}deg`,
      }}
    >
      <span
        style={{
          fontFamily: LOOK.display,
          fontWeight: LOOK.bold,
          fontSize: 32,
          letterSpacing: -0.8,
        }}
      >
        +{copy.money(amount, 0)}
      </span>
    </div>
  );
};

export const Brands: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const copy = useCopy();
  const stage = useStage();
  const g = GRIDS[stage.shape];
  const cells = cellsOf(g);
  const tagged = taggedOf(g, cells);
  const pitch = g.tile + g.gap;

  const enter = spring({ frame, fps, config: { damping: 20, stiffness: 120, mass: 1 } });
  const drift = interpolate(frame, [0, 84], [30, -30]);

  const gridW = g.cols * g.tile + (g.cols - 1) * g.gap;
  const gridH = g.rows * g.tile + (g.rows - 1) * g.gap;

  // Square/wide: title at the top. Tall: title + wall centred as a group.
  const titleTop =
    stage.shape === "tall" ? Math.max(stage.safeTop, (stage.h - TITLE_BLOCK - gridH) / 2) : 96;
  // Wide: the title fits on one line, so the wall moves up.
  const gridTop =
    stage.shape === "tall" ? titleTop + TITLE_BLOCK : stage.shape === "wide" ? 370 : 420;

  return (
    <AbsoluteFill style={{ background: LOOK.sky, overflow: "hidden" }}>
      <div
        style={{
          position: "absolute",
          left: (stage.w - gridW) / 2 + drift,
          top: gridTop + (1 - enter) * 600,
          width: gridW,
          height: gridH,
          rotate: `${g.tilt}deg`,
        }}
      >
        {cells.map((cell) => (
          <div
            key={`${cell.row}-${cell.col}`}
            style={{
              position: "absolute",
              left: cell.col * pitch,
              top: cell.row * pitch,
              width: g.tile,
              height: g.tile,
            }}
          >
            <div
              style={{
                width: g.tile,
                height: g.tile,
                borderRadius: 50,
                overflow: "hidden",
                background: LOOK.white,
                boxShadow: "0 24px 48px -20px rgba(0, 40, 143, 0.35)",
              }}
            >
              {cell.brand.logo ? (
                <Img
                  src={staticFile(cell.brand.logo)}
                  style={{ width: "100%", height: "100%", objectFit: "contain" }}
                />
              ) : null}
            </div>
            {tagged.indexOf(cell) >= 0 ? (
              <RewardTag
                amount={cell.brand.reward}
                at={TAGS_AT + tagged.indexOf(cell) * TAG_STAGGER}
              />
            ) : null}
          </div>
        ))}
      </div>

      {/* Title over a soft fade so tiles never fight the words. */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          height: titleTop + 234,
          background: `linear-gradient(${LOOK.sky} ${titleTop + 141.6}px, rgba(229,236,253,0))`,
        }}
      />
      <div style={{ position: "absolute", top: titleTop, left: 90, right: 90 }}>
        <Words text={copy.brands.title} fontSize={TYPE.title + 4} stagger={2} />
      </div>

      {/* One tick per reward tag landing on the wall. */}
      <Ticks from={TAGS_AT} to={TAGS_AT + TAG_COUNT * TAG_STAGGER} every={TAG_STAGGER} volume={1.4} />
    </AbsoluteFill>
  );
};
